import { useEffect, useMemo, useRef, useState } from 'react'

import { get, isEmpty, isEqual } from 'lodash-es'

import { ProviderInfo } from '../../../models'
import {
  FavoritedProviderProfilesFetchStatus,
  FavoritedProvidersSaveEventPayload,
} from '../../favoritedProviders/types'
import { isProviderAvailableForBooking } from '../../favoritedProviders/utils'

export interface useFavoritedProvidersPageArgs {
  userId: string
  favoritedProvidersByIds: string[] | null
  favoritedProvidersMetadataMap: Record<string, any> | null
  saveUnfavoritedProvider: ({ userId, data }: { userId: string; data: FavoritedProvidersSaveEventPayload }) => void
  fetchProviderProfilesFromFavoritedProvidersIds: ({
    userId,
    providerIds,
  }: {
    userId: string
    providerIds: string[]
  }) => Promise<any>
  validationComplete: boolean | null
  isScreenFocused?: boolean
}

// A hook that takes in the redux state and actions from both web and mobile versions
// of the Favorited Providers page. Generates the props and shared business logic needed for
// the UI component
export const useFavoritedProvidersPage = ({
  userId,
  favoritedProvidersByIds,
  favoritedProvidersMetadataMap,
  saveUnfavoritedProvider,
  fetchProviderProfilesFromFavoritedProvidersIds,
  validationComplete,
  isScreenFocused,
}: useFavoritedProvidersPageArgs) => {
  const lastFetchedProvidersByIds = useRef<string[] | null>(null)

  const [fetchingFavoritedProvidersProfilesStatus, setFetchingFavoritedProvidersProfilesStatus] =
    useState<FavoritedProviderProfilesFetchStatus>(FavoritedProviderProfilesFetchStatus.IDLE)
  const [favoritedProvidersProfilesData, setFavoritedProvidersProfilesData] = useState<any[] | null>(null)

  // Tracks provider IDs the user has unfavorited during the current Favorited Providers page view.
  // Unfavorited providers are immediately hidden from the page without triggering a new API call since we don't need any updated/new data
  // This list persists until the user refreshes the page or later returns to the favorited providers view,
  // at which point a fresh fetch will reflect the updated favorites list
  const [unfavoritedProvidersIdList, setUnfavoritedProvidersIdList] = useState<string[]>([])

  // Mobile -- reset hook props when focusing screen to refetch updated provider profiles if provider list has changed
  useEffect(() => {
    if (isScreenFocused) {
      if (!isEqual(lastFetchedProvidersByIds.current, favoritedProvidersByIds)) {
        setUnfavoritedProvidersIdList([])
        setFetchingFavoritedProvidersProfilesStatus(FavoritedProviderProfilesFetchStatus.IDLE)
      }
    }
  }, [isScreenFocused, favoritedProvidersByIds])

  // Fetches the list of full provider objects given the list of favorited provider IDs
  useEffect(() => {
    if (
      validationComplete !== true ||
      fetchingFavoritedProvidersProfilesStatus !== FavoritedProviderProfilesFetchStatus.IDLE ||
      favoritedProvidersByIds == null ||
      isScreenFocused === false
    )
      return
    const fetchFavoritedProviderFullProfiles = async () => {
      /* If all providers were cleared, we don't need to fetch any provider profiles and can just render to 0 providers page */
      const providerProfiles = await fetchProviderProfilesFromFavoritedProvidersIds({
        userId,
        providerIds: favoritedProvidersByIds,
      })
      setFavoritedProvidersProfilesData(providerProfiles)
      setFetchingFavoritedProvidersProfilesStatus(FavoritedProviderProfilesFetchStatus.DONE)
      // Store the successfully fetched provider IDs
      lastFetchedProvidersByIds.current = favoritedProvidersByIds
    }
    setFetchingFavoritedProvidersProfilesStatus(FavoritedProviderProfilesFetchStatus.PENDING)
    fetchFavoritedProviderFullProfiles()
  }, [
    fetchingFavoritedProvidersProfilesStatus,
    favoritedProvidersByIds,
    userId,
    validationComplete,
    fetchProviderProfilesFromFavoritedProvidersIds,
    isScreenFocused,
  ])

  /* Sort providers by remaining days until expiration, ascending, and separate into available/unavailable */
  const { providers, availableProviders, unavailableProviders } = useMemo(() => {
    if (
      isEmpty(favoritedProvidersProfilesData) ||
      isEmpty(favoritedProvidersMetadataMap) ||
      favoritedProvidersByIds === null
    )
      return { providers: [], availableProviders: [], unavailableProviders: [] }

    const sortedProviders = get(favoritedProvidersProfilesData, '[0].providersInfo', [])
      .filter(
        (provider: ProviderInfo) =>
          favoritedProvidersByIds.includes(provider.lyra_id) && !unfavoritedProvidersIdList.includes(provider.lyra_id),
      )
      .sort((p1: ProviderInfo, p2: ProviderInfo) => {
        // favoritedProvidersMetadataMap holds the timestamp when each provider was favorited
        const p1Metadata = favoritedProvidersMetadataMap[p1.lyra_id]
        const p2Metadata = favoritedProvidersMetadataMap[p2.lyra_id]

        // Sort by createDateUnixTimestamp from oldest to newest
        return p1Metadata.createDateUnixTimestamp - p2Metadata.createDateUnixTimestamp
      })

    // Separate providers into available and unavailable based on their capacity -- determines whether or not they're disabled on the favorited providers page
    const available: ProviderInfo[] = []
    const unavailable: ProviderInfo[] = []

    sortedProviders.forEach((provider: ProviderInfo) => {
      const isProviderAvailable = isProviderAvailableForBooking({ provider })
      if (isProviderAvailable) {
        available.push(provider)
      } else {
        unavailable.push(provider)
      }
    })

    return {
      providers: sortedProviders,
      availableProviders: available,
      unavailableProviders: unavailable,
    }
  }, [
    favoritedProvidersMetadataMap,
    favoritedProvidersProfilesData,
    unfavoritedProvidersIdList,
    favoritedProvidersByIds,
  ])

  /* We can only unfavorite providers from the Favorited Providers page, which is why isFavorited is always set to false */
  const onUnfavoriteFavoritedProvider = ({ providerId }: { providerId: string }) => {
    setUnfavoritedProvidersIdList([...unfavoritedProvidersIdList, providerId])
    saveUnfavoritedProvider({
      userId,
      data: {
        lyraId: userId,
        isFavorited: false,
        providerId,
        searchId: '',
        userFavoriteProviderRequestId: '',
        treatmentPreference: '' as const,
      },
    })
  }

  return {
    favoritedProvidersProfilesData,
    onUnfavoriteFavoritedProvider,
    providers,
    availableProviders,
    unavailableProviders,
    fetchingFavoritedProvidersProfilesStatus,
  }
}
